import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { adminLogin } from '@/hooks/useAdminAuth';

type AdminLoginProps = {
  onSuccess: () => void;
};

export default function AdminLogin({ onSuccess }: AdminLoginProps) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    const result = await adminLogin(email, password);
    setSubmitting(false);
    if (!result.ok) {
      if (result.reason === 'empty') setError(t('admin.login.errorEmpty'));
      else if (result.reason === 'error') setError(t('common.error'));
      else setError(t('admin.login.errorInvalid'));
      return;
    }
    setError(null);
    onSuccess();
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background-50 px-4 py-10">
      <div className="w-full max-w-md animate-fade-rise">
        <div className="mb-6 flex flex-col items-center text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-shield-keyhole-line text-2xl"></i>
          </span>
          <h1 className="mt-4 font-heading text-2xl font-semibold text-foreground-950">
            {t('admin.login.title')}
          </h1>
          <p className="mt-2 text-sm text-foreground-600">{t('admin.login.subtitle')}</p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="rounded-lg border border-background-200 bg-background-50 p-6"
        >
          <div className="space-y-4">
            <div>
              <label htmlFor="admin-email" className="mb-1.5 block text-sm font-semibold text-foreground-800">
                {t('admin.login.email')}
              </label>
              <input
                id="admin-email"
                name="email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder={t('admin.login.emailPh')}
                autoComplete="email"
                className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              />
            </div>
            <div>
              <label htmlFor="admin-password" className="mb-1.5 block text-sm font-semibold text-foreground-800">
                {t('admin.login.password')}
              </label>
              <div className="relative">
                <input
                  id="admin-password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder={t('admin.login.passwordPh')}
                  autoComplete="current-password"
                  className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 pr-11 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute right-2 top-1/2 flex h-7 w-7 -translate-y-1/2 cursor-pointer items-center justify-center rounded-md text-foreground-400 transition-colors hover:text-foreground-700"
                  aria-label={showPassword ? t('bankAuth.hidePassword') : t('bankAuth.showPassword')}
                >
                  <i className={`${showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} text-base`}></i>
                </button>
              </div>
            </div>

            {error && (
              <div className="flex items-start gap-2 rounded-md border border-accent-200 bg-accent-50 px-3 py-2.5 text-sm text-accent-900">
                <i className="ri-error-warning-line mt-0.5"></i>
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 dark:text-foreground-950"
            >
              <i className={submitting ? 'ri-loader-4-line animate-spin' : 'ri-login-box-line'}></i>
              {t('admin.login.submit')}
            </button>
          </div>
        </form>

        <div className="mt-6 text-center">
          <a
            href="/"
            className="inline-flex cursor-pointer items-center gap-1.5 text-sm font-semibold text-foreground-600 transition-colors hover:text-foreground-900"
          >
            <i className="ri-arrow-left-line"></i>
            {t('admin.login.back')}
          </a>
        </div>
      </div>
    </div>
  );
}